<template>
<div class="card-box-full">
  <div class="card-box-header">
    No Reveal Phone Numbers
  </div>

  <!-- Information about consequences -->
  <div class="alert alert-info" style="margin-top: 15px;" role="alert">
    <h6><strong>What happens when a phone number is added to this list:</strong></h6>
    <ul class="mb-0">
      <li>Caller identity lookups will return an empty identity (no name, address, or personal information)</li>
      <li>The reputation of the phone number will be set to neutral</li>
      <li>The robokiller web lookup will return a 404 page for this phone number</li>
      <li>Robokiller/TrapCall will not mark this numbers as spam</li>
      <li>Number Lookup on the TelTech app will return empty results</li>
      <li>Third-party identity providers (Yelp, Everyone API, etc.) will not be queried for this number</li>
    </ul>
  </div>

  <!-- ----------------------------------------------------------------- -->
  <div v-if="errorMessage">
    <div class="alert alert-danger" style="margin-top: 15px; margin-bottom: 5px" role="alert">{{ errorMessage }}</div>
  </div>

  <!-- ----------------------------------------------------------------- -->
  <div v-if="isRefreshing">
    <div class="spinner-border spinner-border-sm text-primary" role="status" style="margin: 13px 0px 0px 10px">
      <span class="sr-only"></span>
    </div>
  </div>

  <!-- ----------------------------------------------------------------- -->
  <div v-else>
    <!-- Search Form -->
    <div class="card" style="margin-top: 15px;">
      <div class="card-header">
        <h5>Search Phone Numbers</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label for="searchInput">Search by phone number or prefix</label>
              <input 
                type="text" 
                class="form-control" 
                id="searchInput"
                v-model="searchQuery" 
                placeholder="e.g., +1234, 555, (555) 123-4567"
                @keyup.enter="performSearch"
                :disabled="isRefreshing"
              />
              <small class="form-text text-muted">
                Enter a phone number, area code, or prefix to search. Use any format - spaces, dashes, and parentheses will be ignored.
              </small>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label>&nbsp;</label>
              <div>
                <button type="button" class="btn btn-primary mr-2" @click="performSearch" :disabled="isRefreshing">
                  <div v-if="isRefreshing" class="spinner-border spinner-border-sm" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <span v-else>Search</span>
                </button>
                <button type="button" class="btn btn-secondary" @click="clearSearch" :disabled="isRefreshing">
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add New Phone Number Form -->
    <div class="card" style="margin-top: 15px;">
      <div class="card-header">
        <h5>Add New Phone Number</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="addPhoneNumber">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="phoneNumber">Phone Number *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="phoneNumber"
                  v-model="newPhoneNumber.phone_number" 
                  placeholder="e.g., +1234567890"
                  required
                  :disabled="isSubmitting"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="comment">Comment</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="comment"
                  v-model="newPhoneNumber.comment" 
                  placeholder="Optional comment about this phone number"
                  :disabled="isSubmitting"
                />
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label>&nbsp;</label>
                <div>
                  <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !newPhoneNumber.phone_number">
                    <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
                      <span class="sr-only">Loading...</span>
                    </div>
                    <span v-else>Add</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Phone Numbers List -->
    <div v-if="phoneNumbers.length > 0">
      <CTabs variant="tabs" :active-tab="0" style="margin-top: 15px">
        <CTab title="Phone Numbers">
          <div class="tab-box" style="padding: 10px">

            <table class="table table-responsive-sm" style="margin-top: 10px; margin-bottom: 0px;">
              <thead>
                <tr style="font-size: 13px; border: 2px solid transparent">
                  <th style="text-align:left">Phone Number</th>
                  <th style="text-align:left">Comment</th>
                  <th style="text-align:left">Created</th>
                  <th style="text-align:center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="phoneNumber in phoneNumbers" :key="phoneNumber.phone_number" style="font-size: 13px">
                  <td>
                    <div style="font-weight: 700; color: #3A9CFC;">
                      {{ phoneNumber.phone_number }}
                    </div>
                  </td>
                  <td>
                    <div style="max-width: 300px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;">
                      {{ phoneNumber.comment || '-' }}
                    </div>
                  </td>
                  <td>
                    <timeago :datetime="phoneNumber.created"></timeago>
                  </td>
                  <td align="center">
                    <button 
                      class="btn btn-sm btn-outline-danger" 
                      @click="confirmDelete(phoneNumber)"
                      :disabled="isSubmitting"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination Controls -->
            <div v-if="pagination.has_next || pagination.has_prev" class="d-flex justify-content-between align-items-center mt-3">
              <div class="text-muted">
                Page {{ pagination.page }} ({{ phoneNumbers.length }} entries)
              </div>
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: !pagination.has_prev }">
                    <button class="page-link" @click="goToPage(pagination.page - 1)" :disabled="!pagination.has_prev || isRefreshing">
                      Previous
                    </button>
                  </li>
                  
                  <!-- Current page indicator -->
                  <li class="page-item active">
                    <span class="page-link">{{ pagination.page }}</span>
                  </li>
                  
                  <li class="page-item" :class="{ disabled: !pagination.has_next }">
                    <button class="page-link" @click="goToPage(pagination.page + 1)" :disabled="!pagination.has_next || isRefreshing">
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
              
              <!-- Page size selector -->
              <div class="d-flex align-items-center">
                <label class="text-muted mr-2 mb-0">Show:</label>
                <select class="form-control form-control-sm" style="width: auto;" v-model="pageSize" @change="changePageSize" :disabled="isRefreshing">
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
              </div>
            </div>

          </div>
        </CTab>
      </CTabs>
    </div>

    <!-- No phone numbers message -->
    <div v-else-if="!isRefreshing" class="alert alert-info" style="margin-top: 15px;" role="alert">
      <span v-if="searchQuery && searchQuery.trim() !== ''">
        No phone numbers found matching "{{ searchQuery }}". Try a different search term or 
        <button type="button" class="btn btn-link p-0" @click="clearSearch">clear the search</button>.
      </span>
      <span v-else>
        No phone numbers found. Add one using the form above.
      </span>
    </div>
  </div>


  <!-- Delete Confirmation Modal -->
  <CModal title="Confirm Delete" color="danger" :show.sync="deleteModal.show">
    <p>Are you sure you want to delete the phone number <strong>{{ deleteModal.phone_number }}</strong>?</p>
    <p v-if="deleteModal.comment"><em>Comment: {{ deleteModal.comment }}</em></p>
    <p class="text-danger">This action cannot be undone.</p>

    <template #footer>
      <CButton @click="deleteModal.show = false" color="secondary" :disabled="isSubmitting">
        Cancel
      </CButton>
      <CButton @click="deletePhoneNumber" color="danger" :disabled="isSubmitting">
        <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <span v-else>Delete</span>
      </CButton>
    </template>
  </CModal>

</div>
</template>

<script>
export default {
  name: 'NoRevealPhoneNumbers',
  data() {
    return {
      errorMessage: null,
      isRefreshing: false,
      isSubmitting: false,
      phoneNumbers: [],
      currentPage: 1,
      pageSize: 50,
      searchQuery: '',
      searchTimeout: null,
      pagination: {
        page: 1,
        limit: 50,
        has_next: false,
        has_prev: false
      },
      newPhoneNumber: {
        phone_number: '',
        comment: ''
      },
      deleteModal: {
        show: false,
        phone_number: '',
        comment: ''
      }
    }
  },
  methods: {
    //--------------------------------------------------------------------------
    refresh(page = null) {
      if (this.isRefreshing) {
        return;
      }
      
      if (page !== null) {
        this.currentPage = page;
      }
      
      this.errorMessage = null;
      this.isRefreshing = true;
      this.phoneNumbers = [];

      var that = this;
      var method = "GET";
      var url = "https://caller-identity.teltechsys.com/no-reveal-phone-numbers?page=" + 
                this.currentPage + "&limit=" + this.pageSize;
      
      // Add search parameter if there's a search query
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        url += "&search=" + encodeURIComponent(this.searchQuery.trim());
      }
      
      var requiresSession = true;
      
      RobocallsAI.shared().execute(method, url, null, requiresSession, {
        onSuccess: function(code, body) {
          that.isRefreshing = false;
          if (code != 200) {
            var message = that.parseMessage(body);
            if (message) {
              that.errorMessage = message;
            } else {
              that.errorMessage = "Received a code " + code + " from the service";
            }
          } else {
            try {
              var result = JSON.parse(body);
              that.phoneNumbers = result.no_reveal_phone_numbers || [];
              that.pagination = result.pagination || that.pagination;
            } catch (err) {
              that.errorMessage = "Failed to parse response from the server";
            }
          }
        },
        onCancelled: function() {
          that.isRefreshing = false;
        },
        onFailure: function(error) {
          that.isRefreshing = false;
          that.errorMessage = error.message || error;
        }
      });
    },
    //--------------------------------------------------------------------------
    addPhoneNumber() {
      if (this.isSubmitting || !this.newPhoneNumber.phone_number) {
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = null;

      var that = this;
      var method = "POST";
      var url = "https://caller-identity.teltechsys.com/no-reveal-phone-numbers";
      
      // Create FormData object for proper form encoding
      var formData = new FormData();
      formData.append('phone_number', this.newPhoneNumber.phone_number);
      formData.append('comment', this.newPhoneNumber.comment || '');
      var body = formData;
      
      var requiresSession = true;

      RobocallsAI.shared().execute(method, url, body, requiresSession, {
        onSuccess: function(code, body) {
          that.isSubmitting = false;
          if (code == 201) {
            // Success - clear form and refresh current page
            that.newPhoneNumber.phone_number = '';
            that.newPhoneNumber.comment = '';
            that.refresh(that.currentPage);
          } else {
            var message = that.parseMessage(body);
            if (message) {
              that.errorMessage = message;
            } else {
              that.errorMessage = "Received a code " + code + " from the service";
            }
          }
        },
        onCancelled: function() {
          that.isSubmitting = false;
        },
        onFailure: function(error) {
          that.isSubmitting = false;
          that.errorMessage = error.message || error;
        }
      });
    },
    //--------------------------------------------------------------------------
    confirmDelete(phoneNumber) {
      this.deleteModal.show = true;
      this.deleteModal.phone_number = phoneNumber.phone_number;
      this.deleteModal.comment = phoneNumber.comment || '';
    },
    //--------------------------------------------------------------------------
    deletePhoneNumber() {
      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      var that = this;
      var method = "DELETE";
      var url = "https://caller-identity.teltechsys.com/no-reveal-phone-numbers/" + encodeURIComponent(this.deleteModal.phone_number);
      var requiresSession = true;

      RobocallsAI.shared().execute(method, url, null, requiresSession, {
        onSuccess: function(code, body) {
          that.isSubmitting = false;
          if (code == 204) {
            // Success - close modal and refresh current page
            that.deleteModal.show = false;
            that.refresh(that.currentPage);
          } else {
            var message = that.parseMessage(body);
            if (message) {
              that.errorMessage = message;
            } else {
              that.errorMessage = "Received a code " + code + " from the service";
            }
          }
        },
        onCancelled: function() {
          that.isSubmitting = false;
        },
        onFailure: function(error) {
          that.isSubmitting = false;
          that.errorMessage = error.message || error;
        }
      });
    },
    //--------------------------------------------------------------------------
    parseMessage(body) {
      try {
        var result = JSON.parse(body);
        return result.message;
      } catch (err) {
        return "";
      }
    },
    //--------------------------------------------------------------------------
    goToPage(page) {
      if (page >= 1 && page !== this.currentPage) {
        this.refresh(page);
      }
    },
    //--------------------------------------------------------------------------
    changePageSize() {
      this.currentPage = 1; // Reset to first page when changing page size
      this.refresh();
    },
    //--------------------------------------------------------------------------
    performSearch() {
      this.currentPage = 1; // Reset to first page when searching
      this.refresh();
    },
    //--------------------------------------------------------------------------
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.refresh();
    },
  },
  mounted() {
    this.refresh();
  },
  beforeDestroy() {
    // Clean up search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}
</script>

<style scoped>
.form-group label {
  font-weight: 600;
  margin-bottom: 5px;
}

.pagination .page-link {
  color: #3A9CFC;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #3A9CFC;
  border-color: #3A9CFC;
}

.pagination .page-link:hover {
  color: #2a7bd4;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
}
</style>
